import * as ReactHelmetAsync from 'react-helmet-async';
const Helmet = ReactHelmetAsync.Helmet || (ReactHelmetAsync as any).default?.Helmet;
import { useParams, useLocation, Link } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { loadBlogPosts } from '@/utils/blogParser.ts';
import { resolveImagePath } from '@/utils/imageMap.ts';

const allPosts = loadBlogPosts();

export function BlogPost() {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  // Fallback: extract slug from pathname for SSG routes that use concrete paths
  const slug = paramSlug || location.pathname.replace(/^\/blog\//, '').replace(/\/$/, '');
  const post = allPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <>
        <div className="min-h-screen bg-gray-50">
          <Navigation />
          <div className="pt-32 pb-16 text-center">
            <h1 className="text-3xl font-heading font-bold mb-4">Post Not Found</h1>
            <Link to="/blog" className="text-primary hover:underline">
              Back to Blog
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const formattedDate = new Date(post.date + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <Helmet>
        <title>{post.title} | Total Tailwheel Blog</title>
        <meta name="description" content={post.snippet} />
        <meta property="og:title" content={`${post.title} | Total Tailwheel`} />
        <meta property="og:description" content={post.snippet} />
        {post.image && (
          <meta property="og:image" content={resolveImagePath(post.image)} />
        )}
        <link rel="canonical" href={`https://totaltailwheel.com/blog/${post.slug}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navigation />

        <article className="pt-28 pb-16">
          <div className="container mx-auto px-4 max-w-3xl">
            {/* Back link */}
            <Link
              to="/blog"
              className="inline-flex items-center text-sm text-primary hover:underline mb-6"
            >
              &larr; Back to Blog
            </Link>

            {/* Title and meta */}
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-3">
              {post.title}
            </h1>
            <p className="text-sm text-gray-500 mb-2">{formattedDate}</p>
            <div className="flex flex-wrap gap-2 mb-8">
              {post.labels.map((label) => (
                <Link
                  key={label}
                  to={`/blog?label=${encodeURIComponent(label)}`}
                  className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full hover:bg-primary/20 transition-colors"
                >
                  {label}
                </Link>
              ))}
            </div>

            {/* Hero image */}
            {post.image && (
              <img
                src={resolveImagePath(post.image)}
                alt={post.imageAlt || post.title}
                className="w-full rounded-lg shadow-md mb-8 object-cover max-h-[500px]"
              />
            )}

            {/* Body */}
            <div
              className="prose prose-lg max-w-none text-gray-700 leading-relaxed
                [&>p]:mb-5"
              dangerouslySetInnerHTML={{ __html: post.contentHtml }}
            />
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
